import { AbilityBuilder, PureAbility, AbilityClass } from '@casl/ability';

/**
 * Frontend mirror of backend/src/modules/platform/rbac/abilities.ts.
 *
 * MUST stay in sync with the backend — the backend is the source of
 * truth + enforcement. This mirror drives UI gating (hide sidebar
 * links, disable buttons) so the UX matches server-side reality.
 */

export type PlatformRole =
    | 'SUPER_ADMIN'
    | 'SALES'
    | 'SUPPORT'
    | 'FINANCE'
    | 'COMPLIANCE'
    | 'DEVOPS'
    | 'READ_ONLY'
    | 'AGENT';

export type PlatformAction = 'manage' | 'create' | 'read' | 'update' | 'delete';
export type PlatformSubject =
    | 'all'
    | 'Merchant'
    | 'Ticket'
    | 'Billing'
    | 'Plan'
    | 'Subscription'
    | 'Invoice'
    | 'Compliance'
    | 'AuditLog'
    | 'System'
    | 'ApiKey'
    | 'PlatformUser'
    | 'Session'
    | 'PlatformSettings'
    | 'Onboarding'
    | 'Cron'
    | 'SystemHealth'
    | 'Deployment'
    | 'MCPCatalog'
    | 'Notification'
    | 'Broadcast'
    | 'Crm'
    | 'FeatureFlag'
    | 'Webhook'
    | 'Integration'
    | 'AgentWorkflow'
    | 'AgentForce'
    | 'Device'
    | 'Report'
    | 'RetentionOffer'
    | 'Cancellation'
    | 'OnCall'
    | 'AbuseSignal'
    | 'ContentBlock'
    | 'SupportImpersonation'
    | 'MerchantActivity'
    | 'Affiliate'
    | 'AffiliatePayout'
    | 'Chat';

export type PlatformAbilityTuple = [PlatformAction, PlatformSubject];
export type PlatformAbility = PureAbility<PlatformAbilityTuple>;
const AbilityCtor = PureAbility as AbilityClass<PlatformAbility>;

export function defineAbilitiesFor(role: PlatformRole | undefined): PlatformAbility {
    const { can, cannot, build } = new AbilityBuilder<PlatformAbility>(AbilityCtor);

    if (!role) return build();

    switch (role) {
        case 'SUPER_ADMIN':
            can('manage', 'all');
            break;
        case 'SALES':
            can('manage', 'Crm');
            can('read', 'AgentForce');
            can('read', 'Broadcast');
            can('read', 'AuditLog');
            can('read', 'PlatformSettings');
            can('read', 'Plan');
            can('read', 'PlatformUser');
            can('read', 'MCPCatalog');
            can('read', 'Notification');
            // Affiliate / partner program — SALES reads partner pipeline.
            can('read', 'Affiliate');
            // Platform Chat (CONTRACTS §6) — every operator role may message.
            can('manage', 'Chat');
            // RBAC-02: no cannot('manage','PlatformUser') here — it shadowed the
            // read grant above (CASL inverted rules win). Backend has none.
            cannot('manage', 'ApiKey');
            cannot('manage', 'Billing');
            cannot('manage', 'System');
            break;
        case 'SUPPORT':
            can('read', 'Merchant');
            can('update', 'Merchant');
            can('manage', 'Ticket');
            can('read', 'AgentForce');
            can('read', 'AuditLog');
            can('read', 'MerchantActivity');
            can('read', 'Session');
            can('read', 'PlatformSettings');
            can('read', 'Plan');
            can('read', 'Subscription');
            can('update', 'Subscription');
            can('read', 'Invoice');
            // Phase 4 O1
            can('manage', 'Onboarding');
            // P5-1 (D-P5-012)
            can('read', 'Cron');
            // P5-4 (D-P5-010)
            can('read', 'SystemHealth');
            // P5-16 (D-P5-006)
            can('read', 'MCPCatalog');
            // P6-1 (D-P6-009)
            can('read', 'Notification');
            // P6-4 (D-P6-009)
            can('read', 'Broadcast');
            can('read', 'Crm');
            // P7-1 / M13
            can('read', 'FeatureFlag');
            // P7-5 / M15
            can('read', 'Webhook');
            // P8-1 / M14
            can('read', 'Integration');
            can('read', 'AgentWorkflow');
            // P9-7 / M17, P9-1 / M18
            can('read', 'Device');
            can('read', 'Report');
            // P10 / M19 + M20 — SUPPORT triages cancellations + acks alerts
            can('manage', 'Cancellation');
            can('read', 'RetentionOffer');
            can('manage', 'OnCall');
            can('manage', 'SupportImpersonation');
            // Platform Chat (CONTRACTS §6)
            can('manage', 'Chat');
            cannot('manage', 'Billing');
            cannot('manage', 'PlatformUser');
            cannot('manage', 'ApiKey');
            break;
        case 'FINANCE':
            can('manage', 'Billing');
            can('read', 'AgentForce');
            can('manage', 'Plan');
            can('manage', 'Subscription');
            can('manage', 'Invoice');
            cannot('delete', 'Invoice');
            can('read', 'Merchant');
            can('read', 'AuditLog');
            can('read', 'PlatformSettings');
            // Phase 4 O1
            can('read', 'Onboarding');
            can('manage', 'Onboarding');
            // P5-1 (D-P5-012)
            can('read', 'Cron');
            // P5-4 (D-P5-010)
            can('read', 'SystemHealth');
            // P6-1 (D-P6-009)
            can('read', 'Notification');
            can('read', 'Crm');
            // Affiliate / partner program — FINANCE owns payouts.
            can('manage', 'AffiliatePayout');
            can('read', 'Affiliate');
            // RBAC-03: revenue-defense playbook (mirrors backend FINANCE block).
            can('read', 'Report');
            can('manage', 'Cancellation');
            can('manage', 'RetentionOffer');
            // Platform Chat (CONTRACTS §6)
            can('manage', 'Chat');
            cannot('manage', 'PlatformUser');
            cannot('manage', 'System');
            cannot('manage', 'ApiKey');
            break;
        case 'COMPLIANCE':
            can('read', 'Merchant');
            can('read', 'AgentForce');
            can('manage', 'Compliance');
            can('read', 'AuditLog');
            can('read', 'MerchantActivity');
            can('read', 'PlatformUser');
            can('read', 'PlatformSettings');
            can('read', 'Plan');
            can('read', 'Subscription');
            can('read', 'Invoice');
            can('read', 'Billing');
            // Phase 4 O1
            can('read', 'Onboarding');
            // P5-1 (D-P5-012)
            can('read', 'Cron');
            // P5-4 (D-P5-010)
            can('read', 'SystemHealth');
            // P5-16 (D-P5-006)
            can('read', 'MCPCatalog');
            // P6-1 (D-P6-009)
            can('read', 'Notification');
            can('read', 'Crm');
            // RBAC-03: COMPLIANCE owns legal/content blocks (mirrors backend).
            can('manage', 'ContentBlock');
            // Platform Chat (CONTRACTS §6)
            can('manage', 'Chat');
            cannot('manage', 'System');
            break;
        case 'DEVOPS':
            can('read', 'Merchant');
            can('manage', 'AgentForce');
            can('manage', 'System');
            can('manage', 'ApiKey');
            can('manage', 'PlatformSettings');
            can('read', 'AuditLog');
            can('read', 'Plan');
            can('read', 'Subscription');
            can('read', 'Invoice');
            can('read', 'Billing');
            // P5-1 (D-P5-012): DEVOPS owns cron operations.
            can('manage', 'Cron');
            // P5-4 (D-P5-010)
            can('read', 'SystemHealth');
            // P5-6 (D-P5-012) + P5-7 (rollback)
            can('manage', 'Deployment');
            // P5-16 (D-P5-006)
            can('read', 'MCPCatalog');
            // P6-1 (D-P6-009)
            can('read', 'Notification');
            can('read', 'Crm');
            // P7-1 / M13 — DEVOPS owns feature flags.
            can('manage', 'FeatureFlag');
            // P7-5 / M15 — DEVOPS manages webhook subscriptions.
            can('manage', 'Webhook');
            // P8-1 / M14 — DEVOPS manages third-party integrations + workflows.
            can('manage', 'Integration');
            can('manage', 'AgentWorkflow');
            // P9-7 / M17, P9-1 / M18
            can('manage', 'Device');
            can('manage', 'Report');
            // P10-5 / M20 — DEVOPS owns on-call rotations
            can('manage', 'OnCall');
            // P11 / M21 + M23
            can('manage', 'AbuseSignal');
            can('manage', 'ContentBlock');
            // Platform Chat (CONTRACTS §6)
            can('manage', 'Chat');
            cannot('manage', 'PlatformUser');
            break;
        case 'READ_ONLY':
            can('read', 'all');
            // Platform Chat (CONTRACTS §6): the viewer role may still start and
            // send chats (owner decision: anyone added may message) — read +
            // create only, no update/manage.
            can('create', 'Chat');
            cannot('read', 'MerchantActivity');
            break;
        case 'AGENT':
            can('read', 'all');
            can('create', 'Crm');
            can('update', 'Crm');
            // Platform Chat (CONTRACTS §6)
            can('manage', 'Chat');
            cannot('read', 'MerchantActivity');
            break;
    }
    return build();
}
